'use client'
import { useMemo, useRef, type FC } from 'react'
import styled from 'styled-components'
import { motion, useInView } from 'motion/react'
import { defaultGrid, designGrid, innerGrid, mq, presets } from '@/styles'
import { Img, Video } from './base'
import { fadeFromLeftVariants, LANG, listItemVariants } from '@/utils'
import type { TypeCmsMedium, TypeLang } from '@/schema'
import { useTranslator } from '@/hooks'
import { useParams } from 'next/navigation'

type Props = {
  data?: {
    title: string
    text: string
    blocks: {
      text: string
    }[]
  }
}

export const getMediaPreview = (medium: TypeCmsMedium) => {
  if (medium?.mediumType === 'image_file' && medium.image) return <Img media={medium.image} className='media' />
  if (medium?.mediumType === 'video_file' && medium.video) return <Video className='media' src={medium.video?.src} autoPlay />
  return <div className='placeholder'></div>
}

const STATIC_BLOCKS = [
  {
    title: {
      es: 'Experiencia acreditada',
      en: 'Proven experience',
      cat: 'Experiència acreditada'
    },
    text: {
      es: 'Más de 30 años en la industria de la producción audiovisual y el casting nos avalan.',
      en: 'Over 30 years in the audiovisual production and casting industry endorse us.',
      cat: 'Més de 30 anys en la indústria de la producció audiovisual i el càsting ens avalen.'
    },
    medium: {
      mediumType: 'image_file',
      image: {
        alt: '',
        xl: {
          src: '/images/grid/grid_00.jpg',
          size: {
            height: 300,
            width: 350
          }
        }
      }
    }
  },
  {
    title: {
      es: 'Escenas profesionales',
      en: 'Professional scenes',
      cat: 'Escenes professionals'
    },
    text: {
      es: 'Producción cinematográfica que convierte tu reel en tu llave a nuevas oportunidades.',
      en: 'Cinematic production that turns your reel into your key to new opportunities.',
      cat: 'Producció cinematogràfica que converteix el teu reel en la teva clau a noves oportunitats.'
    },
    medium: {
      mediumType: 'video_file',
      video: {
        src: '/videos/grid/grid_01.mp4',
      }
    }
  },
  {
    title: {
      es: 'Guiones a medida',
      en: 'Custom scripts',
      cat: 'Guions a mida'
    },
    text: {
      es: 'Historias exclusivas acorde a tus registros actorales y que resaltan tu talento.',
      en: 'Exclusive stories according to your acting records and that highlight your talent.',
      cat: 'Històries exclusives d\'acord amb els teus registres actorals i que ressalten el teu talent.'
    },
    medium: {
      mediumType: 'image_file',
      image: {
        alt: '',
        xl: {
          src: '/images/grid/grid_04.jpg',
          size: {
            height: 300,
            width: 350
          }
        }
      }
    }
  },
  {
    title: {
      es: 'Coaching integral',
      en: 'Integral coaching',
      cat: 'Coaching integral'
    },
    text: {
      es: 'Acompañamiento en todo el proceso. Podemos ponerte en contacto con un coach apropiado para ti o puedes traerte el tuyo.',
      en: 'Support throughout the process. We can put you in touch with a suitable coach for you or you can bring your own.',
      cat: 'Acompanyament en tot el procés. Podem posar-te en contacte amb un coach apropiat per a tu o pots portar-te el teu.'
    },
    medium: {
      mediumType: 'video_file',
      video: {
        src: '/videos/grid/grid_04.mp4',
      }
    }
  },
  {
    title: {
      es: 'Producción prémium',
      en: 'Premium production',
      cat: 'Producció prémium'
    },
    text: {
      es: 'Tecnología de cine digital y profesionales expertos trabajando para que tu producción destaque con máxima calidad.',
      en: 'Digital cinema technology and expert professionals working to make your production stand out with the highest quality.',
      cat: 'Tecnologia de cinema digital i professionals experts treballant perquè la teva producció destaqui amb la màxima qualitat.'
    },
    medium: {
      mediumType: 'image_file',
      image: {
        alt: '',
        xl: {
          src: '/images/grid/grid_02.jpg',
          size: {
            height: 300,
            width: 350
          }
        }
      }
    }
  },
  {
    title: {
      es: 'Espacios únicos',
      en: 'Unique spaces',
      cat: 'Espais únics'
    },
    text: {
      es: 'Platós propios, y múltiples localizaciones reales que se adaptan a tu historia y necesidades.',
      en: 'Own studios, and multiple real locations that adapt to your story and needs.',
      cat: 'Espais propis, i múltiples localitzacions reals que s\'adapten a la teva història i necessitats.',
    },
    medium: {
      mediumType: 'image_file',
      image: {
        alt: '',
        xl: {
          src: '/images/grid/grid_06.jpg',
          size: {
            height: 300,
            width: 350
          }
        }
      }
    }
  },
]

const getItems = (data, lang) => data.map((item, idx) => {
  return (
    <motion.li key={idx} variants={listItemVariants}>
      {getMediaPreview(item.medium)}
      <h3>{item.title[lang]}</h3>
      <p>{item.text[lang]}</p>
    </motion.li>
  )
})

export const SectionGrid:FC<Props> = ({}) => {
  const { lang } = useParams() as { lang: TypeLang }
  const { t } = useTranslator()
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true })

  const headingId = useMemo(() => t('project.internal_page_links')?.[0]?.id?.substring(1) as string, [t])
  const Items = useMemo(() => getItems(STATIC_BLOCKS, lang ?? LANG), [lang])

  return (
    <Section aria-labelledby={headingId} ref={ref}>
      <div>
        <motion.hgroup variants={fadeFromLeftVariants} initial='initial' animate={isInView ? 'animate' : 'initial'}>
          <h2 id={headingId}>{t('project.section_grid.title')}</h2>
          <p>{t('project.section_grid.description')}</p>
        </motion.hgroup>
      </div>
      <motion.ul variants={listItemVariants} initial='initial' animate={isInView ? 'animate' : 'initial'}>
        {Items}
      </motion.ul>
    </Section>
  )
}

const Section = styled(motion.section)`
  ${designGrid({})}
  margin-bottom: 75px;
  overflow-x: hidden;
  padding-top: 75px;
  width: 100vw;
  width: 100dvw;

  ${mq.greaterThan('tablet')} {
    margin-bottom: 110px;
    overflow-x: unset;
    padding-top: 110px;
  }

  > div {
    grid-column: 1 / span 6;

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 5;
    }
  }

  hgroup {
    margin-bottom: 50px;
    text-align: left;

    ${mq.greaterThan('tablet')} {
      margin-bottom: 0;
      position: sticky;
      top: 100px;
    }

    h2,
    p {
      ${presets.font30_35()}
      margin-bottom: 20px;
      text-align: left;
    }
  }

  ul {
    grid-column: 1 / span 6;
    ${innerGrid(defaultGrid.mobile)}

    ${mq.greaterThan('tablet')} {
      grid-column: 6 / span 7;
    }
  }

  li {
    grid-column: auto / span 3;
    margin-bottom: 30px;

    ${mq.greaterThan('tablet')} {
      margin-bottom: 80px;
    }

    p {
      ${presets.font14_15()}
      text-align: center;
    }

    h3,
    p {
      padding: 0 5px;
    }
  }

  h3 {
    ${presets.font20_40()}
    margin-bottom: 10px;
    text-align: center;
  }

  img,
  video,
  .placeholder {
    align-self: stretch;
    border-radius: 20px;
    height: 165px;
    margin-bottom: 10px;
    object-fit: cover;
    pointer-events: none;
    user-select: none;
    width: 100%;

    ${mq.greaterThan('tablet')} {
      height: 300px;
      margin-bottom: 20px;
    }
  }

  .placeholder {
    background-color: var(--color-white);
    opacity: .2;
  }
`
